





































































import { computed, defineComponent, getCurrentInstance, Ref, ref, toRef, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import {
  InputMassMessage,
  InputMessageAttachment,
  MassMessage,
  MassMessageFile,
} from "@scrile/api-provider/dist/api/MassMessageProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { getElement } from "@/lib/elementHelpers";
import { $t } from "@/lib/i18nHelpers";
import { getMediaThumbnailUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { AttachmentFile } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseChip from "@/components/BaseChip";
import BaseIcon from "@/components/BaseIcon";
import GroupSelectedInformer from "@/components/GroupSelectedInformer";
import MessageEditorView from "@/components/MessageEditorView";
import ScheduledDateView from "@/components/ScheduledDateView";
import SendMessageController from "@/components/SendMessageController";
import MassMessageScheduledOption from "./MassMessageScheduledOption.vue";

const useMassMessage = (massMessage: Ref<MassMessage | undefined>) => {
  const messageEditorRef = ref<HTMLDivElement>();
  const value = ref<string>("");
  const pricePackageId = ref<string>("0");
  const previewFiles = ref<AttachmentFile[]>([]);
  const getNowDate = (): string => new Date().toISOString();
  const scheduledTime = ref<string>(getNowDate());
  const selectedGroupIds = ref<string[]>([]);
  const excludedGroupIds = ref<string[]>([]);

  const allowScheduledMassMessages = computed(() => config.featureScheduledMassMessage);

  const vm = getCurrentInstance();
  const syncFilesWithVault = async () => {
    await vm?.$nextTick();

    if (!messageEditorRef.value) return;
    const element = getElement(messageEditorRef.value);
    const event = new Event("filesUpdated");
    element.dispatchEvent(event);
  };
  const updateAttachmentFiles = (cb: (files: AttachmentFile[]) => void) => cb(previewFiles.value);

  const adapterToPreviewFiles = (files: MassMessageFile[]): AttachmentFile[] => {
    return files.map(f => ({
      uid: f.uuid,
      type: f.metadata.contentType,
      src: getMediaThumbnailUrl(f, f.urlPart),
      publicAccess: f.publicAccess,
    }));
  };
  const adaptAttachmentFilesToSave = (attachmentFiles: AttachmentFile[]): InputMessageAttachment[] => {
    return attachmentFiles.map(f => ({
      uuid: f.uid,
      publicAccess: f.publicAccess,
    }));
  };

  watch(
    massMessage,
    newValue => {
      if (!newValue) return;

      value.value = newValue?.text;
      pricePackageId.value = newValue.pricePackageId;
      previewFiles.value = adapterToPreviewFiles(newValue.files);
      scheduledTime.value = newValue.publishTime;
      selectedGroupIds.value = newValue.groupIds;
      excludedGroupIds.value = newValue.excludedGroupIds;

      syncFilesWithVault();
    },
    { immediate: true }
  );

  const { authUserStrict } = useAuthUser();
  const getMassMessageReadyToSave = (): InputMassMessage => {
    return {
      id: massMessage.value?.id ?? undefined,
      text: value.value,
      senderId: authUserStrict.value.id,
      pricePackageId: pricePackageId.value,
      groupIds: selectedGroupIds.value,
      excludedGroupIds: excludedGroupIds.value,
      files: adaptAttachmentFilesToSave(previewFiles.value),
      publishTime: scheduledTime.value,
    };
  };

  return {
    value,
    previewFiles,
    scheduledTime,
    pricePackageId,
    messageEditorRef,
    selectedGroupIds,
    excludedGroupIds,
    allowScheduledMassMessages,
    getNowDate,
    updateAttachmentFiles,
    getMassMessageReadyToSave,
  };
};

const ADMIN_ID = "1";

export default defineComponent({
  name: "MassMessageEditView",
  components: {
    BaseChip,
    BaseIcon,
    BaseButton,
    ScheduledDateView,
    MessageEditorView,
    SendMessageController,
    GroupSelectedInformer,
    MassMessageScheduledOption,
  },
  props: {
    loading: { type: Boolean, default: false },
    massMessage: { type: Object as () => MassMessage },
    groupIds: { type: Array as () => string[] },
    excludedIds: { type: Array as () => string[] },
    recipientCount: { type: Number, default: 0 },
  },
  setup(props, { emit }) {
    const massMessage = computed(() => props.massMessage);

    const {
      value,
      previewFiles,
      scheduledTime,
      pricePackageId,
      messageEditorRef,
      selectedGroupIds,
      excludedGroupIds,
      allowScheduledMassMessages,
      getNowDate,
      updateAttachmentFiles,
      getMassMessageReadyToSave,
    } = useMassMessage(massMessage);

    const isHasMessageToSend = computed<boolean>(() => !!value.value || previewFiles.value.length > 0);

    const isShowSchedule = ref<boolean>(false);
    const onDateSave = (date: Date) => {
      scheduledTime.value = date.toISOString();
      isShowSchedule.value = false;
    };
    const onSubmit = debounce(() => emit("submit", getMassMessageReadyToSave()), 200);
    const onCancelScheduledTime = () => (scheduledTime.value = getNowDate());

    const isScheduledMessage = computed<boolean>(() => {
      return new Date(scheduledTime.value).getTime() >= new Date().getTime() + 1000;
    });
    const buttonText = computed(() => (isScheduledMessage.value ? $t("Save mass message") : $t("Send mass message")));

    watch(
      toRef(props, "groupIds"),
      () => {
        if (props.groupIds) selectedGroupIds.value = props.groupIds;
      },
      { immediate: true }
    );
    watch(
      toRef(props, "excludedIds"),
      () => {
        if (props.excludedIds) excludedGroupIds.value = props.excludedIds;
      },
      { immediate: true }
    );

    return {
      value,
      ADMIN_ID,
      buttonText,
      previewFiles,
      scheduledTime,
      pricePackageId,
      isShowSchedule,
      messageEditorRef,
      selectedGroupIds,
      excludedGroupIds,
      isHasMessageToSend,
      allowScheduledMassMessages,
      onSubmit,
      onDateSave,
      onCancelScheduledTime,
      updateAttachmentFiles,
    };
  },
});
